import React, { useEffect, useContext, useRef, useState, useMemo, useCallback } from 'react';
import { CircularProgress, Avatar, Alert, TextField, InputAdornment, Snackbar } from '@mui/material';
import { Mail, Add, ArrowBack, Inbox, Search } from '@mui/icons-material';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { LoginContext } from '../../../contexts/LoginContext';
import { UserProjectContext } from '../../../contexts/UserProjectContext';
import MessageBubble from '../../commun/messaging/MessageBubble';
import MessageComposer from '../../commun/messaging/MessageComposer';
import LoadMoreButton from '../../commun/messaging/LoadMoreButton';
import ScrollToBottomButton from '../../commun/messaging/ScrollToBottomButton';
import { ConversationSummary } from '../../../interface/Message.interface';
import { useConversations } from '../../../api/messages/useConversations';
import { useMessages } from '../../../api/messages/useMessages';
import { useSendMessage } from '../../../api/messages/useMessageMutations';
import { queryKeys } from '../../../api/queryKeys';

// Admin UUID - to configure according to your backend
const ADMIN_UUID = 'admin';

// ============================================================================
// Styled Components
// ============================================================================

const MessagingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid rgba(102, 126, 234, 0.1);

    @media (max-width: 768px) {
        padding-bottom: 0.5rem;
        flex-wrap: wrap;
        gap: 0.75rem;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    @media (max-width: 768px) {
        font-size: 1.1rem;
    }
`;

const SectionIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiSvgIcon-root {
        color: var(--text-primary);
        font-size: 1.5rem;
    }

    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
        border-radius: 8px;

        .MuiSvgIcon-root {
            font-size: 1rem;
        }
    }
`;

const StatsBar = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 0.75rem 1rem;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.3);

    @media (max-width: 768px) {
        gap: 1rem;
        padding: 0.5rem 0.75rem;
    }
`;

const StatItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .MuiSvgIcon-root {
        font-size: 1.1rem;
        color: var(--primary-color);
        opacity: 0.8;
    }
`;

const StatText = styled.span`
    font-size: 0.9rem;
    color: var(--text-secondary);
    font-weight: 500;

    strong {
        color: var(--text-primary);
        font-weight: 700;
        margin-right: 0.25rem;
    }

    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const StatDivider = styled.div`
    width: 1px;
    height: 20px;
    background: rgba(102, 126, 234, 0.2);
`;

const MessagingLayout = styled.div`
    display: flex;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    height: calc(100vh - 280px);
    min-height: 400px;

    @media (max-width: 1024px) {
        height: calc(100vh - 260px);
    }

    @media (max-width: 768px) {
        height: calc(100vh - 200px);
        min-height: 300px;
        border-radius: 16px;
    }
`;

const Sidebar = styled.div<{ hideOnMobile?: boolean }>`
    width: 340px;
    min-width: 300px;
    border-right: 1px solid rgba(102, 126, 234, 0.1);
    display: flex;
    flex-direction: column;
    background: rgba(248, 250, 252, 0.5);

    @media (max-width: 1024px) {
        width: 300px;
        min-width: 260px;
    }

    @media (max-width: 768px) {
        width: 100%;
        height: 100%;
        border-right: none;
        display: ${props => props.hideOnMobile ? 'none' : 'flex'};
    }
`;

const SidebarHeader = styled.div`
    padding: 1rem 1.25rem;
    border-bottom: 1px solid rgba(102, 126, 234, 0.1);
    background: white;
`;

const SidebarTitle = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
`;

const SearchContainer = styled.div`
    padding: 0.75rem 1rem;
    background: white;
    border-bottom: 1px solid rgba(102, 126, 234, 0.05);
`;

const NewMessageButton = styled.button`
    margin: 0.875rem;
    padding: 0.875rem 1rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.08));
    color: var(--primary-color);
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.9rem;
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        transition: left 0.5s ease;
    }

    &:hover {
        background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.35);

        &::before {
            left: 100%;
        }
    }

    &:active {
        transform: translateY(0);
    }
`;

const ConversationList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
`;

const ConversationItem = styled.div<{ active?: boolean; unread?: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 1rem;
    border-radius: 14px;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    margin-bottom: 0.375rem;
    position: relative;
    background: ${props => {
        if (props.active) return 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1))';
        if (props.unread) return 'rgba(102, 126, 234, 0.04)';
        return 'transparent';
    }};
    border: 1px solid ${props => props.active ? 'rgba(102, 126, 234, 0.2)' : 'transparent'};

    ${props => props.unread && !props.active && `
        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 3px;
            height: 60%;
            background: linear-gradient(180deg, var(--primary-color), var(--secondary-color));
            border-radius: 0 4px 4px 0;
        }
    `}

    &:hover {
        background: ${props => props.active
            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.18), rgba(118, 75, 162, 0.12))'
            : 'rgba(102, 126, 234, 0.08)'};
        transform: translateX(4px);
        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
    }

    &:active {
        transform: translateX(2px) scale(0.99);
    }
`;

const ConversationAvatar = styled(Avatar)`
    width: 48px !important;
    height: 48px !important;
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color)) !important;
    font-weight: 600 !important;
    font-size: 0.9rem !important;
    box-shadow: 0 3px 10px rgba(102, 126, 234, 0.25);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    ${ConversationItem}:hover & {
        transform: scale(1.05);
        box-shadow: 0 4px 14px rgba(102, 126, 234, 0.35);
    }
`;

const ConversationContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const ConversationHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
`;

const ConversationName = styled.span<{ unread?: boolean }>`
    font-weight: ${props => props.unread ? '700' : '600'};
    font-size: 0.95rem;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.2s ease;
`;

const ConversationTime = styled.span<{ unread?: boolean }>`
    font-size: 0.7rem;
    color: ${props => props.unread ? 'var(--primary-color)' : 'var(--text-secondary)'};
    font-weight: ${props => props.unread ? '600' : '500'};
    flex-shrink: 0;
`;

const ConversationPreview = styled.span<{ unread?: boolean }>`
    font-size: 0.85rem;
    color: ${props => props.unread ? 'var(--text-primary)' : 'var(--text-secondary)'};
    font-weight: ${props => props.unread ? '500' : '400'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    line-height: 1.4;
`;

const ConversationBadge = styled.span`
    background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
    color: white;
    font-size: 0.7rem;
    font-weight: 700;
    min-width: 22px;
    height: 22px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 7px;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
    animation: badgePulse 2s ease-in-out infinite;

    @keyframes badgePulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.05); }
    }
`;

const ConversationProject = styled.span`
    font-size: 0.7rem;
    color: var(--primary-color);
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.08));
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    margin-top: 0.375rem;
    display: inline-block;
    font-weight: 500;
    border: 1px solid rgba(102, 126, 234, 0.15);
`;

const MainPanel = styled.div<{ showOnMobile?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    position: relative;

    @media (max-width: 768px) {
        display: ${props => props.showOnMobile ? 'flex' : 'none'};
        width: 100%;
        height: 100%;
    }
`;

const ThreadHeader = styled.div`
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(102, 126, 234, 0.08);
    display: flex;
    align-items: center;
    gap: 1rem;
    background: linear-gradient(180deg, white 0%, rgba(248, 250, 252, 0.8) 100%);
    backdrop-filter: blur(10px);
    flex-shrink: 0;

    @media (max-width: 768px) {
        padding: 0.875rem 1rem;
        gap: 0.75rem;
    }
`;

const BackButton = styled.button`
    display: none;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border: none;
    border-radius: 10px;
    background: rgba(102, 126, 234, 0.1);
    color: var(--primary-color);
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: rgba(102, 126, 234, 0.2);
    }

    &:active {
        transform: scale(0.97);
    }

    @media (max-width: 768px) {
        display: flex;
    }
`;

const ThreadTitle = styled.h3`
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
`;

const ThreadProject = styled.span`
    font-size: 0.8rem;
    color: var(--primary-color);
    background: rgba(102, 126, 234, 0.1);
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    font-weight: 500;
`;

const MessageThread = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.5) 0%, rgba(255, 255, 255, 0.3) 100%);

    @media (max-width: 768px) {
        padding: 1rem;
        gap: 0.75rem;
    }
`;

const DateSeparator = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin: 0.5rem 0;
    padding: 0 1rem;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.2), transparent);
    }
`;

const DateLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0.375rem 1rem;
    background: rgba(102, 126, 234, 0.08);
    border-radius: 20px;
    white-space: nowrap;
`;

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    color: var(--text-secondary);
    text-align: center;
    padding: 2rem 1.5rem;
    padding-top: 3rem;
    background: linear-gradient(180deg, transparent 0%, rgba(102, 126, 234, 0.02) 100%);
    overflow-y: auto;
    min-height: 0;
`;

const EmptyStateIcon = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
    animation: floatIcon 3s ease-in-out infinite;

    &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        border: 2px solid rgba(102, 126, 234, 0.2);
        animation: pulseRing 2s ease-out infinite;
    }

    @keyframes floatIcon {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-4px); }
    }

    @keyframes pulseRing {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(1.3); opacity: 0; }
    }

    .MuiSvgIcon-root {
        font-size: 1.75rem;
        color: var(--primary-color);
        opacity: 0.8;
    }
`;

const EmptyTitle = styled.h3`
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0.75rem 0 0.5rem 0;
    background: linear-gradient(135deg, var(--text-primary), var(--primary-color));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const EmptyDescription = styled.p`
    font-size: 0.9rem;
    max-width: 380px;
    margin: 0 0 0.5rem 0;
    line-height: 1.5;
    color: var(--text-secondary);
`;

const NewMessageComposerWrapper = styled.div`
    width: 100%;
    max-width: 520px;
    margin-top: 1rem;
    padding: 1.25rem 1.5rem;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(102, 126, 234, 0.12);
    flex-shrink: 0;

    @media (max-width: 768px) {
        max-width: 100%;
        padding: 1rem;
        margin-top: 0.75rem;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
`;

// ============================================================================
// Component
// ============================================================================

const UserMessagingView: React.FC = () => {
    // React Query hooks
    const { data: conversationsData, isLoading: loadingConversations, error: conversationsError, refetch: refetchConversations } = useConversations();
    const queryClient = useQueryClient();
    const sendMessageMutation = useSendMessage();

    // Local state
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isComposingNew, setIsComposingNew] = useState(false);

    // Fetch messages for current conversation
    const { data: messagesData, isLoading: loadingMessages } = useMessages(currentConversationId);

    const { currentUser } = useContext(LoginContext);
    const userProjectContext = useContext(UserProjectContext);
    const projects = userProjectContext?.projects || [];
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messageThreadRef = useRef<HTMLDivElement>(null);
    const prevConversationId = useRef<string | null>(null);
    const prevMessagesCount = useRef<number>(0);

    const conversations = conversationsData?.conversations || [];
    const currentMessages = messagesData?.messages || [];
    const error = conversationsError ? (conversationsError as Error).message : null;

    // Scroll to bottom when conversation changes or new message
    useEffect(() => {
        const isNewConversation = prevConversationId.current !== currentConversationId;
        const hasNewMessage = currentMessages.length > prevMessagesCount.current && prevMessagesCount.current > 0;

        if ((isNewConversation || hasNewMessage) && messageThreadRef.current) {
            messageThreadRef.current.scrollTop = messageThreadRef.current.scrollHeight;
        }

        prevConversationId.current = currentConversationId;
        prevMessagesCount.current = currentMessages.length;
    }, [currentConversationId, currentMessages.length]);

    const handleSelectConversation = useCallback((conversation: ConversationSummary) => {
        setIsComposingNew(false);
        setCurrentConversationId(conversation.uuid);
    }, []);

    const handleSendMessage = useCallback(async (content: string, projectUuid?: string): Promise<boolean> => {
        try {
            await sendMessageMutation.mutateAsync({
                recipientId: ADMIN_UUID,
                content,
                projectUuid,
            });
            setSnackbarOpen(true);
            if (isComposingNew) {
                setIsComposingNew(false);
            }
            // Invalidate messages for current conversation
            if (currentConversationId) {
                queryClient.invalidateQueries({ queryKey: queryKeys.messages.conversation(currentConversationId) });
            }
            await refetchConversations();
            return true;
        } catch {
            return false;
        }
    }, [isComposingNew, currentConversationId, sendMessageMutation, refetchConversations, queryClient]);

    const handleNewConversation = useCallback(() => {
        setCurrentConversationId(null);
        setIsComposingNew(true);
    }, []);

    const handleBackFromCompose = useCallback(() => {
        setIsComposingNew(false);
    }, []);

    const clearCurrentConversation = useCallback(() => {
        setCurrentConversationId(null);
    }, []);

    const handleScroll = useCallback(() => {
        if (messageThreadRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messageThreadRef.current;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
            setShowScrollButton(!isNearBottom);
        }
    }, []);

    const scrollToBottom = useCallback(() => {
        if (messageThreadRef.current) {
            messageThreadRef.current.scrollTo({
                top: messageThreadRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, []);

    const getInitials = (name: string): string => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const projectOptions = projects?.map(p => ({
        uuid: p.uuid || '',
        name: p.name || ''
    })) || [];

    const selectedConversation = conversations.find(c => c.uuid === currentConversationId);

    // Group messages by date
    const messagesWithDateGroups = useMemo(() => {
        const result: Array<{ type: 'date' | 'message'; content: string | typeof currentMessages[0] }> = [];
        let lastDateGroup = '';

        currentMessages.forEach((message) => {
            const dateGroup = message.getDateGroup();
            if (dateGroup !== lastDateGroup) {
                result.push({ type: 'date', content: dateGroup });
                lastDateGroup = dateGroup;
            }
            result.push({ type: 'message', content: message });
        });

        return result;
    }, [currentMessages]);

    // Filter conversations by search
    const filteredConversations = conversations.filter(conv => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            conv.participantName.toLowerCase().includes(query) ||
            conv.lastMessage.toLowerCase().includes(query) ||
            (conv.projectName?.toLowerCase().includes(query))
        );
    });

    // Statistics
    const totalConversations = conversations.length;
    const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

    return (
        <MessagingContainer>
            <SectionHeader>
                <HeaderLeft>
                    <SectionIcon>
                        <Mail />
                    </SectionIcon>
                    <SectionTitle>Messagerie</SectionTitle>
                </HeaderLeft>
            </SectionHeader>

            <StatsBar>
                <StatItem>
                    <Inbox />
                    <StatText><strong>{totalConversations}</strong> Conversations</StatText>
                </StatItem>
                <StatDivider />
                <StatItem>
                    <Mail />
                    <StatText><strong>{totalUnread}</strong> Non lus</StatText>
                </StatItem>
            </StatsBar>

            {error && (
                <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    {error}
                </Alert>
            )}

            <MessagingLayout>
                <Sidebar hideOnMobile={!!currentConversationId || isComposingNew}>
                    <SidebarHeader>
                        <SidebarTitle>Messages</SidebarTitle>
                    </SidebarHeader>

                    <SearchContainer>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Rechercher..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(248, 250, 252, 0.8)',
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    </SearchContainer>

                    <NewMessageButton onClick={handleNewConversation}>
                        <Add fontSize="small" />
                        Nouveau message
                    </NewMessageButton>

                    <ConversationList>
                        {loadingConversations && conversations.length === 0 ? (
                            <LoadingContainer>
                                <CircularProgress size={24} />
                            </LoadingContainer>
                        ) : filteredConversations.length === 0 ? (
                            <EmptyState style={{ padding: '1.5rem', minHeight: 'auto' }}>
                                <EmptyStateIcon style={{ width: 56, height: 56 }}>
                                    <Mail style={{ fontSize: '1.5rem' }} />
                                </EmptyStateIcon>
                                <p style={{ fontSize: '0.85rem', margin: 0 }}>
                                    {searchQuery ? 'Aucun resultat' : 'Aucune conversation'}
                                </p>
                            </EmptyState>
                        ) : (
                            <>
                                {filteredConversations.map((conversation) => {
                                    const isUnread = conversation.unreadCount > 0;
                                    return (
                                        <ConversationItem
                                            key={conversation.uuid}
                                            active={currentConversationId === conversation.uuid}
                                            unread={isUnread}
                                            onClick={() => handleSelectConversation(conversation)}
                                        >
                                            <ConversationAvatar>
                                                {getInitials(conversation.participantName)}
                                            </ConversationAvatar>
                                            <ConversationContent>
                                                <ConversationHeader>
                                                    <ConversationName unread={isUnread}>
                                                        {conversation.participantName}
                                                    </ConversationName>
                                                    <ConversationTime unread={isUnread}>
                                                        {conversation.formattedLastMessageAt}
                                                    </ConversationTime>
                                                </ConversationHeader>
                                                <ConversationPreview unread={isUnread}>
                                                    {conversation.lastMessage}
                                                </ConversationPreview>
                                                {conversation.projectName && (
                                                    <ConversationProject>
                                                        {conversation.projectName}
                                                    </ConversationProject>
                                                )}
                                            </ConversationContent>
                                            {isUnread && (
                                                <ConversationBadge>
                                                    {conversation.unreadCount}
                                                </ConversationBadge>
                                            )}
                                        </ConversationItem>
                                    );
                                })}
                            </>
                        )}
                    </ConversationList>
                </Sidebar>

                <MainPanel showOnMobile={!!currentConversationId || isComposingNew}>
                    {currentConversationId && selectedConversation ? (
                        <>
                            <ThreadHeader>
                                <BackButton onClick={clearCurrentConversation}>
                                    <ArrowBack fontSize="small" />
                                    Retour
                                </BackButton>
                                <ThreadTitle>
                                    {selectedConversation.participantName}
                                </ThreadTitle>
                                {selectedConversation.projectName && (
                                    <ThreadProject>
                                        {selectedConversation.projectName}
                                    </ThreadProject>
                                )}
                            </ThreadHeader>

                            <MessageThread ref={messageThreadRef} onScroll={handleScroll}>
                                {loadingMessages ? (
                                    <LoadingContainer>
                                        <CircularProgress size={24} />
                                    </LoadingContainer>
                                ) : currentMessages.length === 0 ? (
                                    <EmptyState>
                                        <EmptyStateIcon>
                                            <Mail />
                                        </EmptyStateIcon>
                                        <EmptyTitle>Aucun message</EmptyTitle>
                                        <EmptyDescription style={{ marginBottom: 0 }}>
                                            Cette conversation ne contient pas encore de messages.
                                        </EmptyDescription>
                                    </EmptyState>
                                ) : (
                                    <>
                                        {messagesWithDateGroups.map((item, index) => (
                                            item.type === 'date' ? (
                                                <DateSeparator key={`date-${index}`}>
                                                    <DateLabel>{item.content as string}</DateLabel>
                                                </DateSeparator>
                                            ) : (
                                                <MessageBubble
                                                    key={(item.content as typeof currentMessages[0]).uuid}
                                                    message={item.content as typeof currentMessages[0]}
                                                    isOwn={(item.content as typeof currentMessages[0]).isSentBy(currentUser?.uuid || '')}
                                                />
                                            )
                                        ))}
                                    </>
                                )}
                                <div ref={messagesEndRef} />
                            </MessageThread>
                            <ScrollToBottomButton visible={showScrollButton} onClick={scrollToBottom} />

                            <MessageComposer
                                onSend={handleSendMessage}
                                disabled={loadingMessages}
                            />
                        </>
                    ) : (
                        <>
                            {isComposingNew && (
                                <ThreadHeader>
                                    <BackButton onClick={handleBackFromCompose}>
                                        <ArrowBack fontSize="small" />
                                        Retour
                                    </BackButton>
                                    <ThreadTitle>Nouveau message</ThreadTitle>
                                </ThreadHeader>
                            )}
                            <EmptyState>
                                <EmptyStateIcon>
                                    <Mail />
                                </EmptyStateIcon>
                                <EmptyTitle>
                                    Envoyer un message à l'administrateur
                                </EmptyTitle>
                                <EmptyDescription>
                                    Posez vos questions ou faites part de vos remarques concernant vos projets.
                                </EmptyDescription>
                                <NewMessageComposerWrapper>
                                    <MessageComposer
                                        onSend={handleSendMessage}
                                        projectOptions={projectOptions}
                                        showProjectSelect={true}
                                        placeholder="Ecrivez votre message à l'administrateur..."
                                        standalone={true}
                                    />
                                </NewMessageComposerWrapper>
                            </EmptyState>
                        </>
                    )}
                </MainPanel>
            </MessagingLayout>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message="Message envoye"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </MessagingContainer>
    );
};

export default UserMessagingView;
